/**
 * Subscription cancellation reasons, kept together rather than spread through
 * the three language files - the same arrangement as the payment statuses in
 * `./statuses`.
 *
 * The keys are `SubscriptionCancellation.REASON_CHOICES` on the backend and
 * travel to it verbatim in the cancel request body; only the wording below is
 * ours. The API also returns a rendered `reason_label`, but it is written in
 * one language, so a Greek super admin would read English labels in the
 * cancellations table. `formatCancellationReason` prefers these and keeps
 * `reason_label` as a fallback for a choice the backend adds before we do.
 */
export const CANCELLATION_REASON_KEYS = [
  'too_expensive',
  'unused',
  'missing_features',
  'too_complex',
  'low_quality',
  'customer_service',
  'switched_service',
  'other',
] as const

export type CancellationReasonKey = (typeof CANCELLATION_REASON_KEYS)[number]

/**
 * The translation key a reason is written under.
 *
 * The backend keys are not used raw: `other` is already a translation key in
 * its own right, and `unused` is generic enough to be claimed by something
 * else later. Prefixing keeps the reasons in their own namespace.
 */
export const cancellationReasonI18nKey = (reason: string): string =>
  `cancellationReason${reason
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('')}`

// A language that misses a reason is a compile error rather than a key name
// rendered into the picker.
type CancellationReasonLabels = Record<
  ReturnType<typeof cancellationReasonI18nKey>,
  string
>

export const cancellationReasonsEn: CancellationReasonLabels = {
  cancellationReasonTooExpensive: 'Too expensive',
  cancellationReasonUnused: 'Not using it enough',
  cancellationReasonMissingFeatures: 'Missing features I need',
  cancellationReasonTooComplex: 'Too complicated',
  cancellationReasonLowQuality: 'Quality not good enough',
  cancellationReasonCustomerService: 'Unhappy with support',
  cancellationReasonSwitchedService: 'Switching to another service',
  cancellationReasonOther: 'Other',
}

export const cancellationReasonsEl: CancellationReasonLabels = {
  cancellationReasonTooExpensive: 'Πολύ ακριβό',
  cancellationReasonUnused: 'Δεν το χρησιμοποιώ αρκετά',
  cancellationReasonMissingFeatures: 'Λείπουν λειτουργίες που χρειάζομαι',
  cancellationReasonTooComplex: 'Πολύ περίπλοκο',
  cancellationReasonLowQuality: 'Η ποιότητα δεν είναι αρκετά καλή',
  cancellationReasonCustomerService: 'Δυσαρεστημένος/η από την υποστήριξη',
  cancellationReasonSwitchedService: 'Αλλάζω σε άλλη υπηρεσία',
  cancellationReasonOther: 'Άλλο',
}

export const cancellationReasonsTr: CancellationReasonLabels = {
  cancellationReasonTooExpensive: 'Çok pahalı',
  cancellationReasonUnused: 'Yeterince kullanmıyorum',
  cancellationReasonMissingFeatures: 'İhtiyacım olan özellikler eksik',
  cancellationReasonTooComplex: 'Çok karmaşık',
  cancellationReasonLowQuality: 'Kalite yeterince iyi değil',
  cancellationReasonCustomerService: 'Destekten memnun değilim',
  cancellationReasonSwitchedService: 'Başka bir hizmete geçiyorum',
  cancellationReasonOther: 'Diğer',
}
